/** Serializes draft writes and drains edits made while a request is in flight. */
export class DraftSaver {
  status = '';
  private revision = 0;
  private savedRevision = 0;
  private timer?: ReturnType<typeof setTimeout>;
  private active?: Promise<boolean>;
  private disposed = false;

  constructor(private readonly write: () => Promise<void>, private readonly failed: () => void) {}

  get pending(): boolean { return this.revision !== this.savedRevision || !!this.active; }
  get saving(): boolean { return !!this.active; }

  changed(): void {
    if (this.disposed) return;
    this.revision++;
    this.status = 'Unsaved changes';
    clearTimeout(this.timer);
    this.timer = setTimeout(() => void this.flush(), 1000);
  }

  flush(): Promise<boolean> {
    clearTimeout(this.timer);
    if (this.active) return this.active;
    this.active = this.drain().finally(() => { this.active = undefined; });
    return this.active;
  }

  private async drain(): Promise<boolean> {
    try {
      while (this.revision !== this.savedRevision && !this.disposed) {
        const revision = this.revision;
        this.status = 'Saving…';
        await this.write();
        this.savedRevision = revision;
      }
      this.status = 'Saved';
      return true;
    } catch {
      this.status = 'Couldn’t save — retry Save';
      this.failed();
      return false;
    }
  }

  /** Stop scheduled writes before deleting a record, and await any in-flight write. */
  async stop(): Promise<void> {
    this.dispose();
    await this.active;
  }

  dispose(): void {
    this.disposed = true;
    clearTimeout(this.timer);
  }
}
